(function ($) {

    // Simple Windows detection for measurement tolerance
    const isWindows = /Windows/i.test(navigator.userAgent);

    $.fn.textfill = function () {

        // Measurement strategy, gated by the LaunchDarkly flag media.text-fill-bounding-rect.
        // The resolved value is emitted as window.textFillConfig.useBoundingRect into every
        // document where .textfill() runs (parent pages, preview iframes, server render).
        // Opt-in: defaults to the legacy display:inline-block + outerWidth/outerHeight path
        // unless the flag is on, in which case the display:inline + Range-based path runs
        // (fixes the float-wrapping gap without integer-rounding issues).
        var useBoundingRect = !!(window.textFillConfig && window.textFillConfig.useBoundingRect);

        var fontSizeProperty = 'font-size';
        // Windows needs a slightly larger tolerance to handle fractional rounding/DPI.
        // Kept platform-only (not flag-gated) so the legacy/flag-off path reproduces the
        // pre-flag behaviour exactly; the new measurement path is happy with 0.75 too.
        var measurementTolerance = isWindows ? 0.75 : 0.5;

        var pixelsPerUnit = { px: 1, pt: 96/72, mm: 96/25.4, in: 96, cm: 96/2.54 };

        function convertToPixels(value, unit)  { return value * (pixelsPerUnit[unit] || 1); }
        function convertFromPixels(pixelValue, targetUnit) { return pixelValue / (pixelsPerUnit[targetUnit] || 1); }
        function parseNumberWithDefault(input, defaultValue) {
            var parsed = parseFloat(input);
            return isNaN(parsed) ? defaultValue : parsed;
        }

        // Determine the decimal precision of a number
        function getDecimalPrecision(value) {
            var str = value.toString();
            if (str.indexOf('.') === -1) return 0; // Integer
            return str.split('.')[1].length;
        }

        // Calculate the appropriate search precision based on min/max values
        function calculateSearchPrecision(minValue, maxValue) {
            var minPrecision = getDecimalPrecision(minValue);
            var maxPrecision = getDecimalPrecision(maxValue);
            var highestPrecision = Math.max(minPrecision, maxPrecision);

            if (highestPrecision === 0) {
                // Both are integers, use 0.1 precision
                return 0.1;
            } else {
                // Use precision based on the most precise value
                return 1 / Math.pow(10, highestPrecision);
            }
        }

        function waitForFontsToLoad() {
            return (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
        }

        // Count only meaningful nodes (elements or non-whitespace text)
        function getMeaningfulChildNodes($containerElement) {
            return $containerElement.contents().filter(function () {
                var isElementNode = this.nodeType === 1;
                var isNonEmptyTextNode = this.nodeType === 3 && this.nodeValue.trim() !== '';
                return isElementNode || isNonEmptyTextNode;
            });
        }

        // Get or create a single element to apply font-size to.
        // When useBoundingRect is on, the wrapper stays display:inline to preserve
        // natural text flow — avoids creating a Block Formatting Context (BFC) that
        // would prevent text from wrapping around floated elements.
        function getTextSizingTarget($hostContainer) {
            var $existingTextWrapper = $hostContainer.children('.__textfill_wrap__');
            if ($existingTextWrapper.length) {
                if (useBoundingRect) {
                    // Convert any existing inline-block wrappers to inline
                    // so they don't create a BFC that breaks float wrapping
                    if ($existingTextWrapper.css('display') === 'inline-block') {
                        $existingTextWrapper.css('display', 'inline');
                    }
                }
                return $existingTextWrapper.eq(0);
            }

            var $meaningfulNodes = getMeaningfulChildNodes($hostContainer);
            if ($meaningfulNodes.length === 1 && $meaningfulNodes[0].nodeType === 1) {
                var $singleElement = $meaningfulNodes.eq(0);

                if (!$singleElement.hasClass('__textfill_wrap__')) {
                    $singleElement.addClass('__textfill_wrap__');
                }

                if (useBoundingRect) {
                    // Do NOT change display — Range-based measurement works for all
                    // display types and keeping display:inline preserves float wrapping.
                } else {
                    // Legacy: promote to inline-block for stable outerWidth/outerHeight measuring
                    if ($singleElement.css('display') === 'inline') {
                        $singleElement.css('display', 'inline-block');
                    }
                }
                return $singleElement;
            }

            // Need a single sizing target: wrap content once
            var existingHTML = $hostContainer.html();
            $hostContainer.empty();
            var wrapperDisplay = useBoundingRect ? 'inline' : 'inline-block';
            var $textWrapper = $('<span class="__textfill_wrap__" style="display:' + wrapperDisplay + ';white-space:inherit;"></span>');
            $textWrapper.html(existingHTML);
            $hostContainer.append($textWrapper);
            return $textWrapper;
        }

        // Detect font unit from CSS first, then data attribute, then default to 'pt'
        function detectFontSizeUnit($textTarget, $hostContainer) {
            // First check the target element's inline style
            var inlineStyleFontSize = ($textTarget[0].style && $textTarget[0].style.fontSize) || '';
            var unitMatch = inlineStyleFontSize.match(/^ *([0-9.+-]+)\s*([a-z%]+) *$/i);
            if (unitMatch) return unitMatch[2].toLowerCase();

            // Check if container has inline style font-size
            var containerInlineStyleFontSize = ($hostContainer[0].style && $hostContainer[0].style.fontSize) || '';
            var containerUnitMatch = containerInlineStyleFontSize.match(/^ *([0-9.+-]+)\s*([a-z%]+) *$/i);
            if (containerUnitMatch) return containerUnitMatch[2].toLowerCase();

            // Fall back to data attribute if provided
            var dataAttributeUnit = $hostContainer.data('font-unit-type');
            if (dataAttributeUnit) return ('' + dataAttributeUnit).toLowerCase();

            // Default to pt (don't use computed CSS as it always returns pixels)
            return 'pt';
        }

        // Pick the element whose vertical overflow defines the height fit.
        // Normally that's the text-fill target itself. But when the target has no height of
        // its own (auto) and wraps to multiple lines, its scrollHeight/clientHeight is
        // computed differently across devicePixelRatios (Retina dpr=2 vs dpr=1), so the
        // fitted size diverges Mac vs Windows. In that case the real, deterministic bound is
        // the nearest CLIPPING ancestor (e.g. `.disclaimer { max-height; overflow:hidden }`),
        // whose height is explicit and dpr-stable — so measure overflow there instead.
        // Scope guards keep every other template on the existing target measurement:
        //   - single-line (white-space:nowrap) targets are width-bound → unchanged
        //   - targets that clip themselves (overflow-y != visible) already have a stable
        //     bound → unchanged
        //   - if no clipping ancestor exists within the template, fall back to the target
        function findHeightBoundElement($hostContainer) {
            var host = $hostContainer[0];
            var hostStyle = window.getComputedStyle(host);
            if (hostStyle.whiteSpace === 'nowrap') return host;
            if (hostStyle.overflowY !== 'visible') return host;

            var el = host.parentElement;
            while (el && el !== document.body && el.id !== 'template-content-wrap') {
                var cs = window.getComputedStyle(el);
                // Only treat an ancestor as the height bound when it BOTH clips and has an
                // explicit max-height — the deterministic bounded-region pattern (e.g.
                // .disclaimer). This avoids redirecting to ancestors that use overflow:hidden
                // incidentally (e.g. border-radius clipping).
                if (cs.overflowY !== 'visible' && cs.maxHeight !== 'none') return el;
                el = el.parentElement;
            }
            return host;
        }

        function resizeTextToFitContainer($hostContainer) {
            var $textTarget = getTextSizingTarget($hostContainer);
            var fontUnit = detectFontSizeUnit($textTarget, $hostContainer);
            var minimumFontSizeInUnit = parseNumberWithDefault($hostContainer.data('min-font-size'), 1);
            var maximumFontSizeInUnit = parseNumberWithDefault($hostContainer.data('max-font-size'), 32);

            // Calculate search precision based on supplied min/max values
            var searchPrecisionInUnit = calculateSearchPrecision(minimumFontSizeInUnit, maximumFontSizeInUnit);
            var searchPrecisionInPixels = convertToPixels(searchPrecisionInUnit, fontUnit);

            // Work in pixels internally for cross-platform consistency
            var minimumFontSizeInPixels = convertToPixels(minimumFontSizeInUnit, fontUnit);
            var maximumFontSizeInPixels = convertToPixels(maximumFontSizeInUnit, fontUnit);

            var containerEl = $hostContainer[0];
            var containerWidth, containerHeight, containerRect;

            if (useBoundingRect) {
                // getBoundingClientRect returns float-precision values — no integer
                // rounding issues. Works for all display modes including inline elements
                // in float-based layouts.
                containerRect = containerEl.getBoundingClientRect();
                containerWidth = containerRect.width;
                containerHeight = containerRect.height;
            } else {
                // Legacy: jQuery outerWidth/outerHeight — consistent with inline-block
                // wrapper measurement below
                containerWidth  = $hostContainer.outerWidth();
                containerHeight = $hostContainer.outerHeight();
            }

            // Height fit is measured on the element that actually clips this text (often the
            // target itself; for an auto-height wrapping target, its clipping ancestor).
            // Width still measures the target/Range below.
            var heightBoundEl = useBoundingRect ? findHeightBoundElement($hostContainer) : containerEl;

            // Exit early if container has no dimensions (hidden, display:none, etc.)
            if (!containerWidth || !containerHeight) return;

            // Stabilize font shaping. These properties neutralize engine-specific defaults
            // (optical sizing, ligature variation, weight synthesis) so the same nominal
            // font-size produces the same binary-search outcome across platforms.
            // - New path (flag on): apply on both Mac and Windows for cross-platform parity.
            // - Legacy path (flag off): keep the pre-flag behaviour of applying on Windows
            //   only, so rollback exactly reproduces prior sizing.
            if (useBoundingRect || isWindows) {
                $textTarget.css({
                    "font-optical-sizing": "none",
                    "font-variant-ligatures": "common-ligatures",
                    "font-synthesis": "none"
                });
            }

            function applyFontSize(fontSizeInPixels) {
                var fontSizeInOriginalUnit = convertFromPixels(fontSizeInPixels, fontUnit);

                // Round to match the precision of input values
                var decimalPlaces = Math.max(
                    getDecimalPrecision(minimumFontSizeInUnit),
                    getDecimalPrecision(maximumFontSizeInUnit)
                );
                if (decimalPlaces === 0) decimalPlaces = 1; // For 0.1 precision with integers

                fontSizeInOriginalUnit = Math.round(fontSizeInOriginalUnit * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);

                $textTarget.css(fontSizeProperty, fontSizeInOriginalUnit + fontUnit);
            }

            // Range over the wrapper's text nodes — used by the useBoundingRect path
            // below to measure the rendered glyph extent (see comment there).
            var measurementRange = document.createRange();
            function measureRenderedText() {
                measurementRange.selectNodeContents($textTarget[0]);
                return measurementRange.getBoundingClientRect();
            }

            // Binary search for largest fitting font size with dynamic precision
            var smallestAcceptableSize = minimumFontSizeInPixels;
            var largestPossibleSize = maximumFontSizeInPixels;

            while (largestPossibleSize - smallestAcceptableSize > searchPrecisionInPixels) {
                var middleSize = (smallestAcceptableSize + largestPossibleSize) / 2;
                applyFontSize(middleSize);

                var textFitsWidth, textFitsHeight;
                if (useBoundingRect) {
                    // Width and height intentionally use DIFFERENT measurement bases:
                    //   - width:  Range.right vs containerRect.right  (viewport coords, getBoundingClientRect)
                    //   - height: scrollHeight vs clientHeight         (layout-box integers)
                    // Each comparison is internally consistent, so iframe zoom / transform:scale()
                    // scales both sides equally and neither check breaks. Do NOT "symmetrize" height
                    // to textRect.bottom — a Range excludes line-height leading and would under-measure
                    // multi-line text, clipping the last lines of a disclaimer (the bug this path fixes).
                    // Likewise never compare a getBoundingClientRect value against scrollHeight/clientHeight.
                    //
                    // Measure the rendered glyph extent via Range, not the wrapper's box.
                    // A Range returns the union of the actual text rects — independent of
                    // display mode and free of the line-box descent inflation that affects
                    // an inline element's own getBoundingClientRect(). This lets the wrapper
                    // stay display:inline (so text wraps around floats) while measuring the
                    // same visible extent on Mac and Windows.
                    // Re-read the container rect live each iteration: applyFontSize() above can
                    // trigger reflow that shifts the container's viewport coords, so both sides of
                    // the width comparison must be measured from the same post-reflow layout state.
                    containerRect = containerEl.getBoundingClientRect();
                    var textRect = measureRenderedText();
                    // Guard against an empty/whitespace-only target returning a zero-width rect:
                    // if the Range reports no width but the container has width, fall back to
                    // the wrapper's outerWidth so width enforcement is not silently bypassed.
                    if (textRect.width === 0 && containerRect.width > 0) {
                        textFitsWidth = $textTarget.outerWidth() <= containerWidth + measurementTolerance;
                    } else {
                        // WIDTH via the Range (glyph extent): lets the wrapper stay display:inline
                        // so text wraps around floats, and measures the same extent on Mac/Windows.
                        textFitsWidth  = textRect.right  <= containerRect.right  + measurementTolerance;
                    }
                    // HEIGHT via the container's true overflow (scrollHeight vs clientHeight),
                    // NOT the Range bottom: a Range excludes line-height leading, so multi-line
                    // text is under-measured vertically and the chosen font overflows the fixed
                    // disclaimer box (last lines get clipped). scrollHeight/clientHeight includes
                    // leading and matches the server render's height check.
                    // scrollHeight/clientHeight are integers, so a sub-pixel layout can leave a
                    // 1px scroll overhang; use at least 1px tolerance here to avoid shrinking the
                    // font for a rounding artifact (the fractional measurementTolerance is for the
                    // float-precision width check above).
                    textFitsHeight = heightBoundEl.scrollHeight <= heightBoundEl.clientHeight + Math.max(measurementTolerance, 1);
                } else {
                    // Legacy: compare wrapper's rendered box against container's jQuery dimensions
                    textFitsWidth  = $textTarget.outerWidth()  <= containerWidth  + measurementTolerance;
                    textFitsHeight = $textTarget.outerHeight() <= containerHeight + measurementTolerance;
                }

                if (textFitsWidth && textFitsHeight) {
                    smallestAcceptableSize = middleSize;
                } else {
                    largestPossibleSize = middleSize;
                }
            }

            applyFontSize(smallestAcceptableSize);
        }

        return this.each(function () {
            var $containerElement = $(this);

            // Early check to prevent processing hidden elements
            if ($containerElement.outerWidth() === 0 || $containerElement.outerHeight() === 0) {
                return;
            }

            waitForFontsToLoad().then(function () {
                resizeTextToFitContainer($containerElement);

                // Automatically refit when container resizes or more fonts load
                if (window.ResizeObserver) {
                    var containerResizeObserver = new ResizeObserver(function() {
                        resizeTextToFitContainer($containerElement);
                    });
                    containerResizeObserver.observe($containerElement[0]);
                }

                $(window).on('resize', function() {
                    resizeTextToFitContainer($containerElement);
                });

                if (document.fonts && document.fonts.addEventListener) {
                    document.fonts.addEventListener('loadingdone', function() {
                        resizeTextToFitContainer($containerElement);
                    });
                }

                // Support for manual triggering via 'text-fill' event
                $containerElement.on('text-fill', function() {
                    resizeTextToFitContainer($containerElement);
                });
            });
        });
    };
})(jQuery);
